import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Visibility as EyeIcon } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import B2bPendingPaymentsApi from 'api/b2b/PendingPayments'
import OrbitSession from 'hooks/useCookies'

import OrderItemModal from './OrderItemsModal'
import type { InvoiceDetailsTableProps } from './PendingPayment'

// Column helper for type safety
const columnHelper = createColumnHelper<InvoiceDetailsTableProps>()

export type OrderListItems = {
  name: string
  count: number
}

export default function InvoiceTable({
  data
}: {
  data: InvoiceDetailsTableProps[]
}) {
  const token = OrbitSession.get('faberToken')
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null)
  const [orderItems, setOrderItems] = useState<OrderListItems[]>([])
  const [showOrderModal, setShowOrderModal] = useState(false)
  const { mutate: getOrderItems } = useMutation(
    (id: string) => B2bPendingPaymentsApi.getOrderItems(token, id),
    {
      onSuccess: (data) => {
        console.log('Order Items Data:', data)
        setOrderItems(data.data.items)
        setShowOrderModal(true)
        setLoadingOrderId(null)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        toast.error(`Failed to fetch the order items. ${error.message}`, {
          autoClose: 2000,
          hideProgressBar: false
        })
        setLoadingOrderId(null)
      }
    }
  )

  // Define columns
  const columns = [
    columnHelper.accessor('company_name', {
      header: 'Company Name',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('order_id', {
      header: 'Order ID',
      cell: (info) => (
        <Typography variant='body2' fontFamily='monospace'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('item_count', {
      header: 'Item Count',
      cell: (info) => {
        const orderId = info.row.original.order_id
        const isThisOrderLoading = loadingOrderId === orderId
        return (
          <Typography variant='body2' fontFamily='monospace'>
            {info.getValue()}
            {isThisOrderLoading ? (
              <CircularProgress />
            ) : (
              <EyeIcon
                sx={{ ml: 1, fontSize: 16 }}
                onClick={() => {
                  setLoadingOrderId(orderId)
                  getOrderItems(orderId)
                }}
              />
            )}
          </Typography>
        )
      }
    }),
    columnHelper.accessor('product', {
      header: 'Product',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('order_date', {
      header: 'Order Date',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.accessor('package_name', {
      header: 'Package Name ',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('package_name', {
      header: 'Due Date',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.accessor('currency', {
      header: 'Currency',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('order_amount', {
      header: 'Order Amount',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('paid_amount', {
      header: 'Paid Amount',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('pending_amount', {
      header: 'Pending Amount',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    })
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table?.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>No Invoice found</Typography>
          </Box>
        )}
      </TableContainer>
      {showOrderModal && (
        <OrderItemModal
          orderItems={orderItems}
          setShowOrderModal={setShowOrderModal}
        />
      )}
    </Box>
  )
}
